import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'api/src/types'
import { useMutation } from '@tanstack/react-query'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { gameStartButtonClassName } from '@/lib/game-start-button-style'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'

type TournamentOutputs = inferRouterOutputs<AppRouter>['tournamentRouter']
type Edition = NonNullable<TournamentOutputs['getEdition']>
type Viewer = TournamentOutputs['getViewer']

export function TournamentRegistration({
  edition,
  viewer,
  refresh,
}: {
  edition: Edition
  viewer: Viewer
  refresh: () => Promise<unknown>
}) {
  const { t } = useI18n()
  const trpc = useTRPC()
  const [acknowledged, setAcknowledged] = useState(false)
  const [failed, setFailed] = useState(false)
  const registration = useMutation(
    trpc.tournamentRouter.register.mutationOptions(),
  )

  useEffect(() => {
    setAcknowledged(false)
  }, [edition.id, edition.rulesAcknowledgementVersion])

  const submit = async () => {
    setFailed(false)
    try {
      await registration.mutateAsync({
        editionId: edition.id,
        rulesAcknowledged: acknowledged,
        rulesVersion: edition.rulesAcknowledgementVersion,
      })
      await refresh()
    }
    catch {
      setFailed(true)
      setAcknowledged(false)
      await refresh().catch(() => {})
    }
  }

  if (viewer.registration) {
    const { phase } = viewer.lifecycle
    const status
      = phase === 'COMPETITION_OPEN'
        ? 'competitionOpen'
        : phase === 'ENDED'
          ? 'ended'
          : phase === 'UNSCHEDULED'
            ? 'placeholderState'
            : 'waiting'
    return (
      <div
        data-testid="tournament-registration-success"
        className="mt-6 grid justify-items-center gap-2 pb-5 pt-3 text-center"
        role="status"
      >
        <CheckCircle2 className="size-[42px] text-primary" aria-hidden="true" />
        <h3 className="text-xl font-semibold">{t('tournament.registered')}</h3>
        <p
          data-testid="tournament-registration-status"
          className="text-muted-foreground"
        >
          {t(`tournament.${status}`)}
        </p>
        {phase === 'ENDED' && !edition.resultsPublished && (
          <p data-testid="tournament-results-pending" className="text-primary">
            {t('tournament.resultsPending')}
          </p>
        )}
      </div>
    )
  }

  if (
    !viewer.registeredAccount
    || viewer.emailEligibility.state !== 'EMAIL_VERIFIED'
    || viewer.lifecycle.registrationClosed
  ) {
    return null
  }

  return (
    <div className="mt-3">
      {viewer.lifecycle.registrationOpen && (
        <label
          htmlFor="tournament-rules-acknowledgement"
          className="flex min-h-11 cursor-pointer items-center gap-3 text-start text-sm"
        >
          <Checkbox
            id="tournament-rules-acknowledgement"
            data-testid="tournament-rules-acknowledgement"
            checked={acknowledged}
            disabled={registration.isPending}
            onCheckedChange={value => setAcknowledged(value === true)}
            className="size-6 border-border focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
          />
          {t('tournament.rulesAcknowledgement')}
        </label>
      )}
      {failed && (
        <p
          role="alert"
          data-testid="tournament-registration-error"
          className="mb-3 text-sm text-primary"
        >
          {t('forum.errorGeneric')}
        </p>
      )}
      <Button
        type="button"
        disabled={!viewer.lifecycle.registrationOpen
          || !acknowledged
          || registration.isPending}
        data-testid="tournament-register"
        onClick={() => {
          void submit()
        }}
        className={`${gameStartButtonClassName} min-h-12 w-full`}
      >
        {registration.isPending && (
          <Loader2 className="me-2 size-4 animate-spin" aria-hidden="true" />
        )}
        {t(
          registration.isPending
            ? 'forum.loading'
            : viewer.lifecycle.registrationOpen
              ? 'tournament.register'
              : viewer.lifecycle.phase === 'UNSCHEDULED'
                ? 'tournament.placeholderState'
                : 'tournament.upcoming',
        )}
      </Button>
    </div>
  )
}
